import Oidc from 'oidc-client';
import { parseLocalStorage } from '@/functions/utility';
import axios from 'axios';

const state = {
  isSigningUp: false,
  authenticated: false,

  forcedShutdown: false,
  APIUser: null,

  openAuthMgr: null,

  ifCheckedAuth: false, //true after first auth check on app load
}


// If we want to take an action after logging out, we can save one of these objects into localStorage, filling in appopriate fields.
const AFTER_LOGOUT_ACTIONS = {
  goToHome: { // just go to root
    type: "goToHome"
  },

  // We use this when a user logs out during lobby after signing in as a temp guest
  // Because if they sign back in, we want to return them to room-lobby.
  loginThenRedirect: {
    type: "loginThenRedirect",
    url: null
  }
}

const getters = {
  origin: () => process.env.VUE_APP_SPACEIN_API_SSO_URL,
  pathPrefix: () => '/sso-api/authentication',

  guestAuthEndpoint: (state, getters) => `${getters.origin}/sso-api/guest-authentication/login`,
  guestRegisterEndpoint: (state, getters, rootState, rootGetters) => `${rootGetters['api/auth/origin']}/AccountUpgrade`,

  isSigningUp: state => state.isSigningUp,
  authenticated: state => state.authenticated,
  forcedShutdown: state => state.forcedShutdown,

  openAuthMgr: state => state.openAuthMgr,

  isLoggedIn: state => state.authenticated && state.APIUser ? true : false,

  config: (state, getters) => {
    return {
      authority: getters.origin,
      client_id: 'spacein/webapp.poc.client',
      redirect_uri: `${location.origin}/signin`,
      response_type: 'code',
      scope: 'spacein.application.clientbroker spacein.application.navigator spacein.application.search spacein.application.chat spacein.application.users spacein.application.notifications spacein.application.media openid profile',
      post_logout_redirect_uri: `${location.origin}/signout-callback`,

      filterProtocolClaims: true,
      loadUserInfo: true,
      automaticSilentRenew: true,
      silent_redirect_uri: `${location.origin}/silent-refresh`,
      guest_redirect_uri: `${location.origin}/guest-signin`,
      userStore: new Oidc.WebStorageStateStore({ store: localStorage })
    }
  },

  ifCheckedAuth: state => state.ifCheckedAuth,
}

const mutations = {
  AUTHENTICATED(state, data) {
    state.authenticated = data;
  },
  IS_SIGNING_UP(state, data) {
    state.isSigningUp = data;
  },
  FORCED_SHUTDOWN(state, data) {
    state.forcedShutdown = data;
  },
  API_USER(state, data) {
    state.APIUser = data;
  },

  IF_CHECKED_AUTH(state, data) {
    state.ifCheckedAuth = data;
  },
}

const actions = {
  async getIsLoggedIn({ state }) {
    return state.APIUser != null && !state.APIUser.expired;
  },

  async initOidc({ state, getters, rootGetters, dispatch }, ifSSORedirect = false) {
    await dispatch('api/connection/init', null, { root: true })

    let openAuthMgr = state.openAuthMgr

    Oidc.Log.logger = console;

    if (!openAuthMgr) {
      state.openAuthMgr = openAuthMgr = new Oidc.UserManager(getters.config);
    }
    openAuthMgr.events.addAccessTokenExpiring(function () {
      console.log('token expiring...')
    });

    openAuthMgr.events.addAccessTokenExpired(function () {
      console.log('token expired...')
    });

    openAuthMgr.events.addSilentRenewError(function () {
      console.log('silent renew error...')

      dispatch('api/connection/reconnect', null, { root: true })
    });
    if (!ifSSORedirect) await dispatch('authenticateThenConnect')
    if ((!getters.authenticated && !rootGetters['routing/isAuthRequiredOnLoad']) || ifSSORedirect) {
      dispatch('loading/loadAppToAuth', null, { root: true })
    }
    if (getters.authenticated) {
      dispatch('api/connection/initWindowEvents', null, { root: true });
    }
  },
  async authenticateThenConnect({ dispatch }, reloadApp = true) {
    console.log('authenticateThenConnect')
    await dispatch('authenticateUser', async () => {
      console.log('authenticated now connecting')
      await dispatch('api/connection/connect', null, { root: true })
      if (reloadApp) {
        console.log('Re-loading the App')
        await dispatch('loading/loadApp', null, { root: true })
      } else {
        console.log('NOT re-loading the App')
      }
    });
  },

  // auth for nav api
  async silentRefreshCallback({ getters, dispatch }) {
    console.log('silent refresh')
    try {
      let openAuthMgr = state.openAuthMgr
      if (!openAuthMgr) {
        state.openAuthMgr = openAuthMgr = new Oidc.UserManager(getters.config);
      }

      await openAuthMgr.signinSilentCallback()

      await dispatch('authenticateThenConnect', false)

      return
    } catch (error) {
      console.log(error)
      throw error
    }
  },
  async signinCallback({ state, getters, dispatch }) {
    console.log('api signin callback')
    try {
      let openAuthMgr = state.openAuthMgr
      if (!openAuthMgr) {
        state.openAuthMgr = openAuthMgr = new Oidc.UserManager(getters.config);
      }

      await openAuthMgr.signinRedirectCallback()

      await dispatch('authenticateThenConnect')

      let redirectUrl = parseLocalStorage('ssoRedirect')
      let redirectTime = redirectUrl?.timestamp
      //if redirect url and was set less than 10 mins ago
      if (redirectUrl && redirectUrl.path && (Date.now() - redirectTime < 1000 * 60 * 10)) dispatch('routing/change', redirectUrl.path, { root: true })
      else dispatch('routing/goToHome', null, { root: true })
      localStorage.removeItem('ssoRedirect')
      return
    } catch (error) {
      console.log("THERE WAS AN ERROR", error)
      let url = new URL(window.location.href)
      if (Object.keys(url.searchParams).length) {
        alert('Authentication Error. Please try again.')
        console.error(error)
      } else if (error.toString().includes('exp')) {
        alert('You cannot log in because the time or date on your device is incorrect. Please change it and try again.')
      } else {
        setTimeout(() => dispatch('routing/goToHome', null, { root: true }), 5000)
        return
      }
    }
  },
  async signinGuestCallback({ getters, rootGetters, commit, dispatch }) {
    console.log('api guest signin callback')
    try {
      await dispatch('authenticateThenConnect')

      await dispatch('guest/handleCheckForGuestInStorage', null, { root: true })

      if (!getters.authenticated && rootGetters['guest/guestStorageData']?.username) {
        //if guest login fails try creating account with username from storage
        await dispatch('guest/existingGuestLoginFailed', null, { root: true })
        return
      }

      let rejoinRoom = false

      let redirectUrl = parseLocalStorage('ssoGuestRedirect')
      let redirectTime = redirectUrl?.timestamp
      //if redirect url and was set less than 10 mins ago
      if (redirectUrl && redirectUrl.path && (Date.now() - redirectTime < 1000 * 60 * 10)) {
        //rejoin room as guest
        if (redirectUrl.path.includes('redirect=rejoin')) {
          redirectUrl.path = redirectUrl.path.replace('redirect=rejoin', '')
          rejoinRoom = true
        }

        if (getters.authenticated) {
          if (rejoinRoom) commit('guest/REJOIN_AFTER_AUTH', true, { root: true })
          else commit('guest/ENTER_AFTER_AUTH', true, { root: true }) //do this after so error doesn't show before redirecting
        } else {
          commit('routing/REQUIRES_AUTH_ON_LOAD', false, { root: true })
          await dispatch('loading/loadAppToAuth', null, { root: true })
        }

        dispatch('routing/change', redirectUrl.path, { root: true })
      }
      else dispatch('routing/goToHome', null, { root: true })
      localStorage.removeItem('ssoGuestRedirect')

      return
    } catch (error) {
      dispatch('popups/guest/errorEnteringGuestLobby', null, { root: true })
    }
  },

  async logUser({ dispatch }, user) {
    let expiry = await dispatch('getAccessTokenExpiry', user);
    console.log('AccessToken expires at: ', expiry);
  },
  async getAccessTokenExpiry(context, user) {
    if (user && user.expires_at) {
      let expireAt = new Date(0); // The 0 there is the key, which sets the date to the epoch
      expireAt.setUTCSeconds(user.expires_at);
      return expireAt;
    } else {
      return null
    }
  },
  async initUser({ dispatch }, user) {
    dispatch('logUser', user);
    await dispatch('setAuthorisedStatus', user);
    return true;
  },
  async setAuthorisedStatus({ getters, commit, dispatch, rootGetters }, user) {
    console.log('set authorised status')
    dispatch('setCheckedAuth')
    let ifAuthenticatedBefore = getters.authenticated ? true : false

    commit('API_USER', user);
    commit('FORCED_SHUTDOWN', false);
    commit('AUTHENTICATED', true);

    //load app if not already or if was unauthenticated before
    if (!ifAuthenticatedBefore) {
      let connectedState = rootGetters['api/connection/isConnected']
      console.log('connected state -', connectedState)
      if (connectedState !== 'Disconnected') dispatch('loading/loadApp', null, { root: true })
    }

    dispatch('getAccessTokenExpiry', user)

    return true;
  },
  async setUnauthorisedStatus({ commit, dispatch }) {
    console.log('unauthorised - user was logged out')
    dispatch('setCheckedAuth')

    commit('API_USER', null)
    commit('FORCED_SHUTDOWN', true)
    commit('AUTHENTICATED', false)
    dispatch('api/connection/closeConnection', null, { root: true })
    dispatch('loading/lockOutClient', null, { root: true })
    return;
  },
  async setCheckedAuth({ getters, commit }) {
    if (!getters.ifCheckedAuth) commit('IF_CHECKED_AUTH', true)
  },
  async getAccessToken({ state }) {
    return state.APIUser?.access_token || '';
  },
  async checkAccessTokenForRequests({ dispatch }) {
    let isLoggedIn = await dispatch('getIsLoggedIn')
    if (isLoggedIn) {
      console.log("AccessToken valid")
      return
    } else {
      console.log("AccessToken invalid")
      await dispatch('authenticateUser')
      return
    }
  },
  async authenticateUser({ state, dispatch }, onSuccess) {
    console.log('authenticateUser')
    try {
      const user = await state.openAuthMgr.getUser();
      console.log('automatic login');
      if (user && !user.expired) {
        console.log('user automatically signed in');
        await dispatch('initUser', user);
        if (onSuccess !== undefined) await onSuccess();
        return true
      } else {
        //try silent refresh
        try {
          const silentUser = await state.openAuthMgr.signinSilent();
          console.log('silent user: ', silentUser)
          if (silentUser && !silentUser.expired) {
            console.log('User silently logged in');
            await dispatch('initUser', silentUser);
            if (onSuccess !== undefined) await onSuccess();
            return true
          } else {
            console.log('silent signin failed - ', 'expired: ', silentUser.expired)
            await dispatch('setUnauthorisedStatus');
            return false
          }
        } catch (error) {
          if (error.message && error.message === 'login_required') {
            await dispatch('setUnauthorisedStatus')
          } else {
            console.log('silent sign in error', error);
            setTimeout(async () => {
              await dispatch('setUnauthorisedStatus')
            }, 3000)
          }
          return false
        }
      }
    } catch (error) {
      console.log('silent sign in error', error);
      dispatch('showAuthenticationError')
      setTimeout(async () => {
        await dispatch('setUnauthorisedStatus')
      }, 3000)
      return false
    }
  },

  async showAuthenticationError({ dispatch }) {
    dispatch('popups/show/error', {
      message: 'There was an error logging in. <br></br> If the issue persists please contact support at <u>support@spacein.co.uk</u>.'
    }, { root: true })
  },
  async loginWithSSO({ state }, returnUrl = `${location.pathname}${location.search}`) {
    let returnData = {
      path: returnUrl,
      timestamp: Date.now()
    }
    if (returnUrl.includes('/signin')) returnData.path = '/'
    localStorage.setItem('ssoRedirect', JSON.stringify(returnData))
    state.openAuthMgr.signinRedirect()
  },
  async loginAsGuest({ getters, dispatch }, [userId, signInToken, redirectParam = false]) {
    let query = location.search || ''
    if (redirectParam) {
      if (query) query = `${query}&redirect=${redirectParam}`
      else query = `?redirect=${redirectParam}`
    }
    //called from guest room lobby
    let returnUrl = `${location.pathname}${query}`
    let returnData = {
      path: returnUrl,
      timestamp: Date.now()
    }
    //if fails don't use 'guest-signin' as redirect
    let existingReturn = parseLocalStorage('ssoGuestRedirect')
    if (existingReturn?.path && returnUrl.includes('/guest-signin')) returnData.path = existingReturn.path

    localStorage.setItem('ssoGuestRedirect', JSON.stringify(returnData))
    dispatch('analytics/loginAsGuest', [userId, signInToken, returnData], { root: true })

    let form = document.createElement('form')
    form.setAttribute('action', getters.guestAuthEndpoint)
    form.setAttribute('method', 'post')
    form.setAttribute('type', 'hidden')

    let userInput = document.createElement('input')
    userInput.setAttribute('name', 'userId')
    userInput.setAttribute('value', userId)
    userInput.setAttribute('type', 'hidden')
    form.appendChild(userInput)

    let tokenInput = document.createElement('input')
    tokenInput.setAttribute('name', 'signInToken')
    tokenInput.setAttribute('value', signInToken)
    tokenInput.setAttribute('type', 'hidden')
    form.appendChild(tokenInput)

    let submit = document.createElement('input')
    submit.setAttribute('type', 'button')
    submit.setAttribute('value', 'Submit')
    submit.setAttribute('type', 'hidden')
    form.appendChild(submit)
    document.querySelector('.app').appendChild(form)

    form.submit()
  },
  async registerAsGuest({ getters, dispatch }, [userId, signInToken, comp = 'body']) {
    dispatch('analytics/createGuestAccount', [userId, signInToken], { root: true })
    if (!comp) comp = 'body' //backup for when null
    //called from guest room lobby when clicking signup to spacein
    let form = document.createElement('form')
    form.setAttribute('action', getters.guestRegisterEndpoint)
    form.setAttribute('method', 'post')
    form.setAttribute('type', 'hidden')

    let userInput = document.createElement('input')
    userInput.setAttribute('name', 'userId')
    userInput.setAttribute('value', userId)
    userInput.setAttribute('type', 'hidden')
    form.appendChild(userInput)

    let tokenInput = document.createElement('input')
    tokenInput.setAttribute('name', 'signInToken')
    tokenInput.setAttribute('value', signInToken)
    tokenInput.setAttribute('type', 'hidden')
    form.appendChild(tokenInput)

    let submit = document.createElement('input')
    submit.setAttribute('type', 'button')
    submit.setAttribute('value', 'Submit')
    submit.setAttribute('type', 'hidden')
    form.appendChild(submit)
    document.querySelector(comp).appendChild(form)

    form.submit()
  },
  async signoutCallback({ dispatch }) {
    const afterLogoutAction = parseLocalStorage("afterLogoutAction") || AFTER_LOGOUT_ACTIONS.goToHome
    console.log("afterLogoutAction: " + JSON.stringify(afterLogoutAction))
    localStorage.removeItem("afterLogoutAction")

    switch (afterLogoutAction.type) {
      case "goToHome":
        dispatch('routing/goToHome', null, { root: true })
        break
      case "loginThenRedirect":
        // go back to login screen, then redirect to given URL
        console.log("logging in then redirecting to", afterLogoutAction.url)
        dispatch('loginWithSSO', afterLogoutAction.url)
        break
      default:
        console.log("unknown afterLogoutAction type: " + afterLogoutAction.type)
        dispatch('routing/goToHome', null, { root: true })
    }
  },
  async logout({ state, commit, dispatch }, afterLogoutAction = AFTER_LOGOUT_ACTIONS.goToHome) {
    try {
      // Tells the client what to do after logging out and returning to signout-callback
      localStorage.setItem("afterLogoutAction", JSON.stringify(afterLogoutAction))

      dispatch('routing/goToHome', null, { root: true })

      const user = await state.openAuthMgr.getUser();
      await state.openAuthMgr.signoutRedirect({ 'id_token_hint': user.id_token })
      commit('AUTHENTICATED', false)
      dispatch('loading/unload', null, { root: true })
      dispatch('analytics/logout', null, { root: true })
      return
    } catch (error) {
      console.log('logout error', error);
      throw error;
    }
  },
  async logoutAPI({ state, getters, dispatch }) {
    try {
      let res = await axios.get(getters.origin + getters.pathPrefix + '/logout', { withCredentials: true })
      dispatch('setUnauthorisedStatus')
      state.openAuthMgr.removeUser()
      return res;
    } catch (error) {
      console.log('logout error', error);
      throw error;
    }
  },
  async logoutGuestOnDisconnect({ state, dispatch, rootGetters }) {
    if (!rootGetters['current_user/isExternalGuest']) return
    document.cookie = '.AspNetCore.Identity.Application=; Path=/;  Domain=.' + process.env.VUE_APP_DOMAIN + '; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=None; Secure';
    state.openAuthMgr.removeUser()
    await dispatch('setUnauthorisedStatus')
    dispatch('analytics/logoutGuestOnDisconnect', null, { root: true })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
